import { create } from "zustand";

export interface CityBuilding {
  position: number;
  buildingType: string;
  level: number;
  isUpgrading: boolean;
  upgradeEndAt: string | null;
}

export interface CityUnit {
  unitType: string;
  count: number;
}

export interface CityState {
  id: number;
  name: string;
  isCapital: boolean;
  islandId?: number;
  islandName?: string;
  wood: number;
  wine: number;
  marble: number;
  crystal: number;
  sulfur: number;
  gold: number;
  populationMax: number;
  populationCurrent: number;
  forestWorkers: number;
  mineWorkers: number;
  scientists: number;
  happiness: number;
  islandLuxury?: string;
  buildings: CityBuilding[];
  garrison: CityUnit[];
  trainingQueue: { id: number; unitType: string; count: number; endAt: string }[];
}

export interface Research {
  researchType: string;
  level: number;
  isResearching: boolean;
  researchEndAt: string | null;
}

export interface UserState {
  id: number;
  telegramId: string;
  displayName: string;
  username?: string;
  gold: number;
  researchPoints: number;
  tradeShips: number;
  tradeShipsUsed: number;
}

interface GameStore {
  // Auth state
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // User data
  user: UserState | null;
  cities: CityState[];
  researches: Research[];

  // Current selection
  activeCityId: number | null;

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAuth: (user: UserState, cities: CityState[], researches: Research[]) => void;
  setActiveCity: (cityId: number) => void;
  updateCity: (city: Partial<CityState> & { id: number }) => void;
  updateResources: (cityId: number, resources: Partial<Pick<CityState, "wood" | "wine" | "marble" | "crystal" | "sulfur" | "gold">>) => void;
  setResearches: (researches: Research[]) => void;
  updateResearch: (researchType: string, updates: Partial<Research>) => void;
}

export const useGameStore = create<GameStore>((set) => ({
  isAuthenticated: false,
  isLoading: true,
  error: null,
  user: null,
  cities: [],
  researches: [],
  activeCityId: null,

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  setAuth: (user, cities, researches) =>
    set({
      isAuthenticated: true,
      isLoading: false,
      user,
      cities,
      researches,
      activeCityId: cities[0]?.id ?? null,
    }),

  setActiveCity: (cityId) => set({ activeCityId: cityId }),

  updateCity: (partial) =>
    set((state) => ({
      cities: state.cities.map((c) =>
        c.id === partial.id ? { ...c, ...partial } : c
      ),
    })),

  updateResources: (cityId, resources) =>
    set((state) => ({
      cities: state.cities.map((c) =>
        c.id === cityId ? { ...c, ...resources } : c
      ),
    })),

  setResearches: (researches) => set({ researches }),

  updateResearch: (researchType, updates) =>
    set((state) => ({
      researches: state.researches.map((r) =>
        r.researchType === researchType ? { ...r, ...updates } : r
      ),
    })),
}));
